import React from "react";
import {ErrorType} from "../../../../enums";
import {InputUtils} from "../../../../helpers";
import {Colors, Container, IconError, IconInfo, Text} from "../../../../ui";
import {Dialog} from "../../../bounce-ui";
import {ErrorOrigin} from "../utils";

export interface IPaymentFailedDialogContentProps {
  error: string;
  errorReason?: string;
  errorReasonText: string;
  errorExplanationText: string;
  showTipBox?: boolean;
  errorOrigin?: ErrorOrigin;
  errorCode?: string;
  secondaryButtonText?: string;
  onClickSecondary?: () => void;
}

export const getExplanationText = (error: string): string => {
  if (error === ErrorType.EMAIL_ALREADY_EXISTS) {
    return "Please change email or contact us.";
  }
  return "You can try again with a different payment method or chat with an expert to find the right solution for you.";
};

interface IPaymentFailedContent {
  errorReasonText: string;
  errorExplanationText: string;
  showTipBox: boolean;
  errorOrigin?: ErrorOrigin;
  errorDescriptionText?: string;
}

export const PaymentFailedContent = ({
  errorReasonText,
  errorExplanationText,
  showTipBox,
  errorOrigin,
  errorDescriptionText,
}: IPaymentFailedContent) => {
  return (
    <Container fullWidth sx={{gap: "8px", alignItems: "start"}}>
      <Text variant={"inter600"}>Reason: {errorReasonText}</Text>
      <Text>{errorExplanationText}</Text>
      {showTipBox && (
        <Container
          fullWidth
          sx={{
            alignItems: "start",
            backgroundColor: Colors.grey._100,
            borderRadius: "8px",
            padding: "12px",
            gap: "6px",
            marginTop: "8px",
          }}
        >
          <Container row sx={{gap: "8px"}}>
            <IconInfo color={Colors.grey._1000} />
            <Text size={14} variant={"inter600"} color={Colors.grey._1000}>
              Error origin: {errorOrigin ? InputUtils.capitalizeString(errorOrigin) : ""}
            </Text>
          </Container>
          {errorDescriptionText && (
            <Text size={14} color={Colors.grey._1000}>
              {errorDescriptionText}
            </Text>
          )}
        </Container>
      )}
    </Container>
  );
};

export const PaymentFailedDialogContent = ({
  error,
  errorReason,
  errorReasonText,
  errorExplanationText,
  showTipBox = false,
  errorOrigin,
  errorCode,
  secondaryButtonText,
  onClickSecondary,
}: IPaymentFailedDialogContentProps) => {
  const isCreditCardBlocked = errorCode === "credit_card_not_allowed";
  const showErrorDescription = !isCreditCardBlocked && error && error !== errorReason && error !== errorExplanationText;
  const errorDescriptionText = showErrorDescription
    ? `Error description: ${error}${errorCode ? ` [${errorCode}]` : ""}`
    : undefined;

  const resolvedExplanationText = isCreditCardBlocked
    ? "Enter your debit card details to complete the payment, or select a different payment method to proceed."
    : errorExplanationText;

  return (
    <Dialog
      image={<IconError style={{width: "44px", height: "44px"}} />}
      title={"Payment unsuccessful"}
      buttonText={"Got it"}
      secondaryButtonText={secondaryButtonText}
      onClickSecondary={onClickSecondary}
    >
      <PaymentFailedContent
        errorReasonText={errorReasonText}
        errorExplanationText={resolvedExplanationText}
        showTipBox={showTipBox}
        errorOrigin={errorOrigin}
        errorDescriptionText={errorDescriptionText}
      />
    </Dialog>
  );
};
